/**
 * Subscription Service
 *
 * Handles all subscription-related API calls for Hebrew Hearts premium subscriptions.
 * Integrated with Stripe for payment processing.
 */

import { loadStripe } from '@stripe/stripe-js';
import { API_BASE_URL } from '../config/runtimeConfig';

// Initialize Stripe
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    if (!stripeKey) {
      console.warn('VITE_STRIPE_PUBLIC_KEY not configured');
      return null;
    }
    stripePromise = loadStripe(stripeKey);
  }
  return stripePromise;
};

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
};

/**
 * Fetch current user's subscription status from backend
 * @returns {Promise<Object>} Subscription data
 */
export const fetchSubscriptionStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subscription status');
    }

    const data = await response.json();
    return {
      success: true,
      subscription: data,
    };
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return {
      success: false,
      error: error.message,
      subscription: {
        isPremium: false,
        tier: 'free',
        status: 'none',
      },
    };
  }
};

/**
 * Get available subscription tiers
 * @returns {Promise<Object>} Available tiers
 */
export const getSubscriptionTiers = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/tiers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subscription tiers');
    }

    const data = await response.json();
    return {
      success: true,
      tiers: data.tiers,
    };
  } catch (error) {
    console.error('Error fetching tiers:', error);
    return {
      success: false,
      error: error.message,
      tiers: [],
    };
  }
};

/**
 * Create a Stripe checkout session and redirect to checkout
 * @param {string} tier - The tier to subscribe to ('basic', 'premium', 'elite')
 * @returns {Promise<Object>} Result of checkout creation
 */
export const upgradeToPremium = async (tier) => {
  try {
    // Create checkout session
    const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({ tier }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();

    // Redirect to Stripe Checkout
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe not initialized');
    }

    const { error } = await stripe.redirectToCheckout({
      sessionId: data.sessionId,
    });

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Upgrade failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Create a checkout session (alternative method that returns URL)
 * @param {Object} plan - The plan to upgrade to
 * @returns {Promise<Object>} Checkout session data
 */
export const createCheckoutSession = async (plan) => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({
        tier: plan.id || plan.tier,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return {
      success: true,
      sessionId: data.sessionId,
      checkoutUrl: data.url,
    };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Change subscription tier (upgrade/downgrade)
 * @param {string} newTier - The new tier ('basic', 'premium', 'elite')
 * @returns {Promise<Object>} Result of tier change
 */
export const changeTier = async (newTier) => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/change-tier`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({ newTier }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to change tier');
    }

    const data = await response.json();
    return {
      success: true,
      subscription: data.subscription,
      message: data.message,
    };
  } catch (error) {
    console.error('Change tier failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Update subscription (alias for changeTier)
 * @param {string} newPlanId - The new plan/tier ID
 * @returns {Promise<Object>} Result of update
 */
export const updateSubscription = async (newPlanId) => {
  return changeTier(newPlanId);
};

/**
 * Cancel subscription at period end
 * @param {string} reason - Optional cancellation reason
 * @returns {Promise<Object>} Result of cancellation
 */
export const cancelSubscription = async (reason = null) => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel subscription');
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message,
      subscription: data.subscription,
    };
  } catch (error) {
    console.error('Cancellation failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Reactivate a canceled subscription
 * @returns {Promise<Object>} Result of reactivation
 */
export const reactivateSubscription = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/reactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reactivate subscription');
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message,
      subscription: data.subscription,
    };
  } catch (error) {
    console.error('Reactivation failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Open Stripe billing portal for subscription management
 * @returns {Promise<Object>} Result of portal creation
 */
export const openBillingPortal = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/billing-portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to open billing portal');
    }

    const data = await response.json();

    // Redirect to billing portal
    window.location.href = data.url;

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error opening billing portal:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get subscription history
 * @returns {Promise<Object>} Payment/subscription history
 */
export const getPaymentHistory = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/history`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment history');
    }

    const data = await response.json();
    return {
      success: true,
      payments: data.history,
    };
  } catch (error) {
    console.error('Error fetching payment history:', error);
    return {
      success: false,
      error: error.message,
      payments: [],
    };
  }
};

/**
 * Apply promo code (placeholder - implement if you add promo codes to Stripe)
 * @param {string} promoCode - The promo code to apply
 * @returns {Promise<Object>} Result of applying promo code
 */
export const applyPromoCode = async (promoCode) => {
  try {
    // TODO: Implement promo code support via Stripe coupons
    console.warn('Promo code functionality not yet implemented');
    return {
      success: false,
      error: 'Promo codes not yet supported',
    };
  } catch (error) {
    console.error('Error applying promo code:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export default {
  fetchSubscriptionStatus,
  getSubscriptionTiers,
  createCheckoutSession,
  upgradeToPremium,
  changeTier,
  cancelSubscription,
  reactivateSubscription,
  updateSubscription,
  openBillingPortal,
  getPaymentHistory,
  applyPromoCode,
};
